import { DEFAULTS } from './defaults'
import { STRATEGY_META } from './strategy'
import type { SimulationInputs } from '../types'

const STORAGE_KEY = 'option-strategy-lab:inputs:v1'

const ENUM_VALUES: Partial<Record<keyof SimulationInputs, readonly string[]>> = {
  strategy: Object.keys(STRATEGY_META),
  strikeMethod: ['delta', 'otm', 'atm', 'fixed'],
  sizingMode: ['fixed_units', 'equity_rebalance'],
  ivModel: ['constant', 'mean_reverting', 'stress_regime'],
  contractRounding: ['fractional', 'floor', 'nearest'],
  insolvencyMode: ['continue_fixed_units', 'stop_new_risk'],
  settlementStyle: ['cash', 'physical_expiry'],
  marginMode: ['off', 'reg_t_approx'],
}

/**
 * Accepts only known keys with the right type (and a known value for enums), so
 * a hand-edited link or stale storage can never inject a malformed input.
 */
export function sanitizeInputs(raw: Record<string, unknown>): Partial<SimulationInputs> {
  const patch: Record<string, unknown> = {}
  for (const key of Object.keys(DEFAULTS) as (keyof SimulationInputs)[]) {
    if (!(key in raw)) continue
    const value = raw[key]
    const allowed = ENUM_VALUES[key]
    if (allowed) {
      if (typeof value === 'string' && allowed.includes(value)) patch[key] = value
    } else {
      const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
      if (Number.isFinite(number)) patch[key] = number
    }
  }
  return patch as Partial<SimulationInputs>
}

/** Encodes only the fields that differ from the defaults, keeping share links short. */
export function encodeInputs(inputs: SimulationInputs): string {
  const params = new URLSearchParams()
  for (const key of Object.keys(DEFAULTS) as (keyof SimulationInputs)[]) {
    if (inputs[key] !== DEFAULTS[key]) params.set(key, String(inputs[key]))
  }
  return params.toString()
}

export function decodeInputs(query: string): Partial<SimulationInputs> {
  const params = new URLSearchParams(query.replace(/^[#?]/, ''))
  return sanitizeInputs(Object.fromEntries(params.entries()))
}

export function loadInitialInputs(): SimulationInputs {
  if (typeof window === 'undefined') return DEFAULTS
  const fromUrl = decodeInputs(window.location.hash)
  if (Object.keys(fromUrl).length) return { ...DEFAULTS, ...fromUrl }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored) return { ...DEFAULTS, ...sanitizeInputs(JSON.parse(stored)) }
  } catch {
    // Storage can be unavailable (private mode) or hold malformed JSON; fall back to defaults.
  }
  return DEFAULTS
}

export function saveInputs(inputs: SimulationInputs): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs))
  } catch {
    // Persistence is a convenience only.
  }
}

export function shareUrl(inputs: SimulationInputs): string {
  const hash = encodeInputs(inputs)
  return window.location.origin + window.location.pathname + (hash ? '#' + hash : '')
}

/** Mirrors the inputs of the displayed result into the address bar without adding history entries. */
export function syncUrl(inputs: SimulationInputs): void {
  const hash = encodeInputs(inputs)
  window.history.replaceState(null, '', window.location.pathname + window.location.search + (hash ? '#' + hash : ''))
}
